import { toast } from 'sonner';
import type { ICommand } from '../../../core/commands/ICommand';
import type {
  CanvasPlugin,
  CanvasPluginConnectionCommitResult,
  CanvasPluginHostContext,
} from '../../../core/canvas/CanvasPlugin';
import type { Edge, Node } from '../../../core/models/scene';
import { CreateConnectorCommand } from '../../../core/tools/standard/ConnectorTool';

export type ConnectorAnchorSide = 'top' | 'right' | 'bottom' | 'left';

export type CanvasConnectionCommitPlan =
  | { action: 'skip' }
  | {
      action: 'org-reporting';
      fromId: string;
      toId: string;
      dotted: boolean;
      fromAnchor?: ConnectorAnchorSide;
      toAnchor?: ConnectorAnchorSide;
    }
  | {
      action: 'org-matrix-reporting';
      fromId: string;
      toId: string;
      fromAnchor?: ConnectorAnchorSide;
      toAnchor?: ConnectorAnchorSide;
    }
  | {
      action: 'generic';
      fromId: string;
      toId: string;
      connectionType: string;
      warning?: string;
    };

export type PlanCanvasConnectionCommitInput = {
  plugins: readonly Pick<CanvasPlugin, 'resolveConnectionCommit'>[];
  diagramType: CanvasPluginHostContext['diagramType'] | null | undefined;
  sourceNodeId: string;
  targetNodeId: string;
  sourceNode?: Pick<Node, 'id' | 'type'> | null;
  targetNode?: Pick<Node, 'id' | 'type'> | null;
  startAnchor?: ConnectorAnchorSide | string | null;
  endAnchor?: ConnectorAnchorSide | string | null;
  currentConnectionType: string | null | undefined;
  nodes: readonly Node[];
  edges: readonly Edge[];
  context: CanvasPluginHostContext;
  extra?: Record<string, unknown>;
};

const isConnectorAnchorSide = (value: string | null | undefined): value is ConnectorAnchorSide => (
  value === 'top' || value === 'right' || value === 'bottom' || value === 'left'
);

const mapAnchorsForResolvedEndpoints = (input: {
  fromId: string;
  toId: string;
  sourceNodeId: string;
  targetNodeId: string;
  startAnchor?: ConnectorAnchorSide | string | null;
  endAnchor?: ConnectorAnchorSide | string | null;
}): { fromAnchor: ConnectorAnchorSide; toAnchor: ConnectorAnchorSide } | null => {
  const startAnchor = isConnectorAnchorSide(input.startAnchor) ? input.startAnchor : null;
  const endAnchor = isConnectorAnchorSide(input.endAnchor) ? input.endAnchor : null;
  if (!startAnchor || !endAnchor) return null;
  if (input.fromId === input.sourceNodeId && input.toId === input.targetNodeId) {
    return { fromAnchor: startAnchor, toAnchor: endAnchor };
  }
  if (input.fromId === input.targetNodeId && input.toId === input.sourceNodeId) {
    return { fromAnchor: endAnchor, toAnchor: startAnchor };
  }
  return null;
};

export const planCanvasConnectionCommit = (
  input: PlanCanvasConnectionCommitInput,
): CanvasConnectionCommitPlan => {
  const currentConnectionType = input.currentConnectionType || 'line';
  const resolved: CanvasPluginConnectionCommitResult = (() => {
    for (const plugin of input.plugins) {
      const result = plugin.resolveConnectionCommit?.({
        diagramType: input.diagramType,
        sourceNodeId: input.sourceNodeId,
        targetNodeId: input.targetNodeId,
        sourceNodeType: input.sourceNode?.type,
        targetNodeType: input.targetNode?.type,
        startAnchor: input.startAnchor,
        currentConnectionType,
        nodes: input.nodes,
        edges: input.edges,
        context: input.context,
        extra: input.extra,
      });
      if (result) return result;
    }
    return {
      fromId: input.sourceNodeId,
      toId: input.targetNodeId,
      connectionType: currentConnectionType,
      isOrgReporting: false,
    };
  })();

  const fromId = resolved.fromId;
  const toId = resolved.toId;
  let connectionType = resolved.connectionType;
  const existingEdge = input.edges.find((edge) => edge.from === fromId && edge.to === toId);

  if (
    !fromId
    || !toId
    || fromId === toId
    || (fromId === input.sourceNodeId && toId === input.sourceNodeId)
    || existingEdge
  ) {
    return { action: 'skip' };
  }

  let isOrgReporting = Boolean(resolved.isOrgReporting);
  if (input.sourceNode?.type === 'image' || input.targetNode?.type === 'image') {
    connectionType = 'line';
    isOrgReporting = false;
  }

  if (isOrgReporting) {
    const mappedAnchors = mapAnchorsForResolvedEndpoints({
      fromId,
      toId,
      sourceNodeId: input.sourceNodeId,
      targetNodeId: input.targetNodeId,
      startAnchor: input.startAnchor,
      endAnchor: input.endAnchor,
    });
    return {
      action: 'org-reporting',
      fromId,
      toId,
      dotted: connectionType === 'dotted_line_reports',
      ...(mappedAnchors ?? {}),
    };
  }

  if (input.diagramType === 'org' && connectionType === 'matrix_reports') {
    const mappedAnchors = mapAnchorsForResolvedEndpoints({
      fromId, toId, sourceNodeId: input.sourceNodeId, targetNodeId: input.targetNodeId,
      startAnchor: input.startAnchor, endAnchor: input.endAnchor,
    });
    return { action: 'org-matrix-reporting', fromId, toId, ...(mappedAnchors ?? {}) };
  }

  const warning = resolved.extra?.connectionWarning;
  return {
    action: 'generic',
    fromId,
    toId,
    connectionType,
    ...(typeof warning === 'string' && warning ? { warning } : {}),
  };
};

export const executeCanvasConnectionPlan = (
  plan: CanvasConnectionCommitPlan,
  deps: {
    connectorTool: {
      execute: (params: { from: string; to: string; type: string }) => ICommand | null;
    };
    commandManager: { execute: (command: ICommand) => unknown };
    ensureOrgDeps: () => Promise<{
      CreateReportsToCommand: new (params: {
        fromId: string;
        toId: string;
        dotted?: boolean;
        fromAnchor?: ConnectorAnchorSide;
        toAnchor?: ConnectorAnchorSide;
      }) => unknown;
      CreateMatrixReportsCommand: new (params: {
        fromId: string;
        toId: string;
        fromAnchor?: ConnectorAnchorSide;
        toAnchor?: ConnectorAnchorSide;
      }) => unknown;
    }>;
  },
): void => {
  if (plan.action === 'skip') return;

  if (plan.action === 'org-reporting') {
    const capturedFrom = plan.fromId;
    const capturedTo = plan.toId;
    const dotted = plan.dotted;
    const fromAnchor = plan.fromAnchor;
    const toAnchor = plan.toAnchor;
    void deps.ensureOrgDeps().then((orgDeps) => {
      const reportsToCmd = new orgDeps.CreateReportsToCommand({
        fromId: capturedFrom,
        toId: capturedTo,
        dotted,
        ...(fromAnchor ? { fromAnchor } : {}),
        ...(toAnchor ? { toAnchor } : {}),
      }) as ICommand;
      deps.commandManager.execute(reportsToCmd);
    });
    return;
  }

  if (plan.action === 'org-matrix-reporting') {
    void deps.ensureOrgDeps().then((orgDeps) => {
      const command = new orgDeps.CreateMatrixReportsCommand({
        fromId: plan.fromId,
        toId: plan.toId,
        ...(plan.fromAnchor ? { fromAnchor: plan.fromAnchor } : {}),
        ...(plan.toAnchor ? { toAnchor: plan.toAnchor } : {}),
      }) as ICommand;
      deps.commandManager.execute(command);
    });
    return;
  }

  if (plan.warning) {
    toast.warning(plan.warning);
  }
  const command = deps.connectorTool.execute({
    from: plan.fromId,
    to: plan.toId,
    type: plan.connectionType,
  });
  if (command) {
    deps.commandManager.execute(command);
  }
};

export const executeIncompleteCanvasConnection = (
  edge: Edge,
  deps: { commandManager: { execute: (command: ICommand) => unknown } },
): void => {
  deps.commandManager.execute(new CreateConnectorCommand(edge));
};
